import { FormInput, InputProps } from '@systemeio/ui'
import { ChangeEvent, ForwardedRef, forwardRef } from 'react'
import {
  Controller,
  ControllerProps,
  FieldPath,
  FieldValues,
  PathValue,
  UseFormSetValue,
} from 'react-hook-form'

type FormInputRhfProps<TValues extends FieldValues, TName extends FieldPath<TValues>> = InputProps &
  Omit<ControllerProps<TValues, TName>, 'render'> & { setValue: UseFormSetValue<TValues> }

function FormInputRhfBase<TValues extends FieldValues, TName extends FieldPath<TValues>>(
  props: FormInputRhfProps<TValues, TName>,
  ref?: ForwardedRef<HTMLInputElement>,
) {
  const { setValue, control, name, rules, ...restProps } = props

  const handleChange = (event: ChangeEvent<HTMLInputElement>) =>
    setValue(name, event.target.value as PathValue<TValues, TName>)

  return (
    <Controller
      control={control}
      name={name}
      rules={rules}
      render={({ field }) => (
        <FormInput {...restProps} ref={ref} value={field.value} onChange={handleChange} />
      )}
    />
  )
}

const FormInputRhf = forwardRef(FormInputRhfBase) as <
  TValues extends FieldValues,
  TName extends FieldPath<TValues>,
>(
  props: FormInputRhfProps<TValues, TName> & { ref?: ForwardedRef<HTMLInputElement> },
) => ReturnType<typeof FormInputRhfBase>

export default FormInputRhf
